import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { ApiService } from '@frontend/sports/common/api-utils';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { takeTop } from '@frontend/sports/common/core/data-access/sport-model';
import { usePreloaderData } from '@frontend/sports/common/core/utils/preloaded-data';
import { SportsProductService } from '@frontend/sports/host-app/sports-product/feature/sports-product-service';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { WINDOW } from '@frontend/vanilla/core';
import { linkedTransferState } from '@frontend/vanilla/ssr';
import { Observable, map, of, switchMap, tap } from 'rxjs';

import { SiteMapResponse, SitemapItemModel, SitemapModel } from './sitemap.models';

@Injectable({
    providedIn: 'root',
})
export class SitemapService {
    private readonly api = inject(ApiService);
    private readonly sportsProductService = inject(SportsProductService);
    private readonly sportsCache = inject(SportsCacheService);
    private readonly sitecore = inject(Sitecore);
    private readonly urlHelper = inject(UrlHelperService);
    private readonly _window = inject(WINDOW);
    private isServer = isPlatformServer(inject(PLATFORM_ID));

    private readonly _sitemapState = linkedTransferState<SitemapModel>('sitemapState', { items: [] });
    private readonly _sitemapObs$ = toObservable(this._sitemapState);

    private readonly _sitemap$ = this.sportsProductService.isSportsProductActive$.pipe(
        switchMap((isSportsActive) => (isSportsActive ? this._sitemapObs$ : of({ items: [] }))),
        tap((sitemap) => {
            if (this.isServer) {
                this._sitemapState.set(sitemap);
            }
        }),
    );

    get sitemap$(): Observable<SitemapModel> {
        return this._sitemap$;
    }

    @usePreloaderData({
        preloader: function (this: SitemapService) {
            return this._window.MS2JS?.SiteMapLoader;
        },
    })
    getSiteMapConfiguration(): Observable<SiteMapResponse> {
        return this.api.get<SiteMapResponse>('sitemap', undefined, { transferCache: true }).pipe(
            switchMap((sitemap) => {
                if (!sitemap.data?.length && !sitemap.competitionFallback?.length) {
                    return this.getDefaultConfiguration();
                }
                return of(sitemap);
            }),
        );
    }

    setSitemap(sitemap: SitemapModel): void {
        this._sitemapState.set(sitemap);
    }

    private getDefaultConfiguration(): Observable<SiteMapResponse> {
        const liveItem = <SitemapItemModel>{
            id: '{9E50F332-672C-4EB0-9F8B-DE15978848AC}', //just random id
            icon: 'theme-spot-filled sitemap-red-icon',
            type: 'Live',
            title: this.sitecore.globalMessages.NavLive,
            level: 1,
            showIcon: true,
            url: this.urlHelper.getLiveUrl(),
            children: [],
        };

        return this.sportsCache.getSportList().pipe(
            map((items) => {
                const sportsItems = takeTop(items, 4).map((item) => {
                    return <SitemapItemModel>{
                        id: item.id.toString(),
                        sportId: item.id,
                        level: 1,
                        showIcon: true,
                        type: 'Sport',
                        title: item.name,
                        url: item?.isVirtual ? this.urlHelper.getVirtualSportUrl(item) : this.urlHelper.getSportUrl(item),
                        children: [],
                    };
                });

                return {
                    data: [liveItem, ...sportsItems],
                    competitionFallback: [],
                };
            }),
        );
    }
}
